/** Ticket de báscula con datos de pesaje, transporte, lote y barco. */
export interface ScaleTicket {
  /** Número de ticket. */
  ticket_number?:             number;
  /** Identificador del camión. */
  trucks_id:                  number;
  /** Placas del tractor. */
  plates:                     string;
  /** Placas del primer remolque. */
  trailer_plates1:            string;
  /** Placas del segundo remolque. */
  trailer_plates2:            string;
  /** Número de caja/van. */
  van_number:                 string;
  /** Nombre del operador. */
  operator_name:              string;
  /** Peso de origen. */
  origin_weight:              number;
  /** Fecha de entrada. */
  entry_date:                 string;
  /** Peso de entrada. */
  input_weight:               number;
  /** Fecha de salida. */
  departure_date:             string;
  /** Peso de salida. */
  output_weight:              number;
  /** Peso neto. */
  net_weight:                 number;
  /** Tipo de operación. */
  operation_type:             number;
  /** Número de lote (puede ser nulo). */
  lot_number:                 number | null;
  /** Identificador del módulo (puede ser nulo). */
  module_id:                  number | null;
  /** Identificador del grupo de material (puede ser nulo). */
  material_group_id:          number | null;
  /** Identificador del material (puede ser nulo). */
  material_id:                number | null;
  /** Número de barco (puede ser nulo). */
  ship_number:                number | null;
  /** Nombre del barco. */
  ship_name:                  string;
  /** Identificador del tipo de transporte. */
  transport_type_id:          number;
  /** Identificador del remolque (puede ser nulo). */
  trailer_id:                 number | null;
  /** Remolque del cliente. */
  customer_trailer:           number;
  /** Número de cita. */
  appointment_number:         number;
  /** UUID de Koura. */
  uuid_koura?:                string;
  /** Nombre del grupo de material. */
  material_group_name?:       string;
  /** Nombre del lote. */
  lot_name?:                  string;
  /** Nombre del lote (alterno). */
  batch_name?:                string;
  /** Identificador del catálogo de báscula. */
  scale_catalog_id:           number;
  /** Identificador del supervisor de báscula. */
  scale_supervisors_id:       number;
  /** Estatus del ticket (puede ser nulo). */
  status?:                    boolean | null;
  /** Peso bruto (puede ser nulo). */
  gross_weight?:              number | null;
  /** Peso de tara (puede ser nulo). */
  tare_weight?:               number | null;
  /** Estatus del ticket. */
  status_ticket:              boolean;
  /** Estatus de envío de datos a Koura. */
  status_koura_data_sending:  boolean;
  /** Rutas internas/exportación. */
  routes_int_exp:             number;
  /** Año del lote (puede ser nulo). */
  batch_year?:                number | null;
}

/** Remolque con sus placas y operador. */
export interface Trailer {
  /** Identificador del remolque. */
  id?:              number;
  /** Placas del remolque. */
  plates:           string;
  /** Nombre del operador. */
  drivers_name:     string;
  /** Descripción del remolque. */
  description?:     string;
}

/** Barco filtrado por módulo, con tipo de operación y datos del barco. */
export interface BoatsByModule {
  /** Tipo de operación. */
  operarion_type: number;
  /** Número del barco. */
  boats_number:   number;
  /** Nombre del barco. */
  boats_name:     string;
  /** Fecha asociada. */
  date?:          string;
}

/** Peso de tara asociado a unas placas. */
export interface TareWeight {
  /** Identificador del registro. */
  id?:              number;
  /** Peso de tara. */
  tare_weight:      number;
  /** Placas asociadas. */
  plates:           string;
}

/** Respuesta de operaciones de báscula, incluyendo la respuesta de Koura. */
export interface ScaleResponse {
  /** Identificador del registro afectado. */
  id?:                number;
  /** Indica si la operación produjo error. */
  error?:             boolean;
  /** Mensaje descriptivo del resultado. */
  message?:           string;
  /** Tipo de respuesta. */
  Tipo?:              string;
  /** Mensaje de la respuesta. */
  Mensaje?:           string;
  /** Valores devueltos por Koura. */
  Valor?:             KouraResponse[];
}

/** Respuesta individual de Koura con su tipo y mensaje. */
export interface KouraResponse {
  /** Tipo de respuesta. */
  Tipo:              string;
  /** Mensaje de la respuesta. */
  Mensaje:           string;
}

/** Peso de origen registrado para unas placas, con su referencia y fecha. */
export interface OriginWeight {
  /** Identificador del registro. */
  id?:                number;
  /** Placas asociadas. */
  plates:             string;
  /** Referencia del peso de origen. */
  reference:          string;
  /** Fecha del peso de origen. */
  date_origin_weight: string;
  /** Peso de origen. */
  origin_weight:      number;
  /** Descripción. */
  description?:       string;
}

/** Respuesta de descarga de un ticket de báscula. */
export interface DownloadScaleResponse {
  /** URL para acceder al archivo. */
  url?:               string;
  /** Contenido del archivo en base64. */
  file_base64?:       string;
  /** Indica si la operación produjo error. */
  error?:             boolean;
  /** Mensaje descriptivo del resultado. */
  message?:           string;
}

/** Parámetros para generar un ticket genérico. */
export interface GenericTicket {
  /** Módulo del ticket. */
  module_id:      Module;
  /** Tipo de operación. */
  operation_type: OperationType;
  /** Identificador de la operación. */
  operation_id:   number;
  /** Identificador del ticket (puede ser nulo). */
  id_ticket:      number | null;
  /** Identificador del peso (puede ser nulo). */
  id_weight:      number | null;
}

/** Módulos disponibles para un ticket genérico. */
export enum Module {
  /** Importación. */
  import= 1,
  /** Exportación. */
  export,
  /** Cabotaje. */
  cabotage
}

/** Tipos de operación de un ticket genérico. */
export enum OperationType {
  /** Entrada. */
  entry= 1,
  /** Salida. */
  output
}

/** Respuesta con la URL del ticket genérico generado. */
export interface GenericTicketResponse {
  /** URL para acceder al ticket. */
  url: string;
}

/** Báscula (weighing machine) con su modelo y estatus de edición. */
export interface WeighingMachinee {
  /** Identificador de la báscula. */
  id:                number;
  /** Descripción de la báscula. */
  description:       string;
  /** Modelo de la báscula. */
  model:             string;
  /** Estatus de edición. */
  editing_status:    boolean;
}

/** Configuración de básculas internas y externas. */
export interface Scale {
  /** Identificadores de básculas internas. */
  internal_scale:   number[];
  /** Identificadores de básculas externas. */
  external_scale:   number[];
}

/** Ticket de origen con su descripción. */
export interface OriginTicket {
  /** Identificador del ticket de origen. */
  id:               number;
  /** Descripción del ticket de origen. */
  description:      string;
}

/** Cuerpo de solicitud para SYBASA con datos del ticket y báscula. */
export interface SybasaBody {
  /** Identificador del tipo de transporte. */
  transport_type_id:  number;
  /** Número de ticket. */
  ticket_number:      number;
  /** Clave de la báscula. */
  scale_key:          string;
  /** Año. */
  year:               number;
}

/** Ticket de báscula ferroviario proveniente de SYBASA. */
export interface ScaleTicketRailwaySybasa {
  /** Identificador del ticket. */
  id:               number;
  /** Identificador del grupo de tickets. */
  group_tickets_id: number;
  /** Fecha de entrada. */
  entry_date:       string;
  /** Fecha de salida. */
  departure_date:   string;
  /** Unidad. */
  unit:             string;
  /** Número. */
  number:           string;
  /** Peso de entrada. */
  input_weight:     number;
  /** Peso de salida. */
  output_weight:    number;
  /** Peso bruto. */
  gross_weight:     number;
  /** Peso de tara. */
  tare_weight:      number;
  /** Peso neto. */
  net_weight:       number;
  /** Clave de la terminal. */
  terminal_key:     string;
  /** Clave del destino. */
  destination_key:  string;
  /** Clave del producto. */
  product_key:      string;
  /** Identificador de la línea ferroviaria. */
  railway_line_id:  string;
}

/** Ticket de báscula de camión proveniente de SYBASA. */
export interface ScaleTicketTruckSybasa {
  /** Identificador del ticket. */
  id:                  string;
  /** Identificador de la báscula. */
  weighing_machine_id: string;
  /** Año. */
  year:                number;
  /** Número de ticket. */
  ticket_number:       number;
  /** Fecha de entrada. */
  entry_date:          string;
  /** Fecha de salida. */
  departure_date:      string;
  /** Placas del tractor. */
  plates:              string;
  /** Placas del primer remolque. */
  trailer_plates1:     string;
  /** Placas del segundo remolque. */
  trailer_plates2:     string;
  /** Operador. */
  driver:              string;
  /** Pesador. */
  weigher:             string;
  /** Peso bruto. */
  gross_weight:        number;
  /** Peso de tara. */
  tare_weight:         number;
  /** Peso neto. */
  net_weight:          number;
  /** Peso de origen. */
  origin_weight:       number;
  /** Tipo de transporte. */
  transport_type:      string;
  /** Referencia. */
  reference:           string;
  /** Lote. */
  lot:                 string;
  /** Clave de la terminal. */
  terminal_key:        string;
  /** Clave del destino. */
  destination_key:     string;
  /** Clave del producto. */
  product_key:         string;
  /** Estatus. */
  status:              string;
}

/** Configuración de un overlay (mensaje superpuesto) en la pantalla de báscula. */
export interface ScaleTicketOverlay {
  /** Mensaje a mostrar. */
  message: string;
  /** Icono a mostrar. */
  icon: string;
  /** Indica si se muestran botones. */
  buttons: boolean;
  /** Tiempo de visualización (ms). */
  time?: number;
}

/** Tipo de camión: interno o externo. */
export enum TruckType {
  /** Camión interno. */
  Internal = 1,
  /** Camión externo. */
  External
}

/** Parámetros para obtener tickets en una actualización masiva. */
export interface TicketsBulkUpdateGetParams {
  /** Tipo de operación. */
  operation_type: number;
  /** Número de lote. */
  lot_number: number;
  /** Rutas internas/exportación. */
  routes_int_exp?: number;
}

/** Parámetros para actualizar masivamente un conjunto de tickets. */
export interface TicketsBulkUpdateParams {
  /** Números de los tickets a actualizar. */
  ticket_numbers: number[],
  /** Número de barco a asignar. */
  ship_number?: number,
  /** Número de lote a asignar. */
  lot_number?: number,
  /** Identificador del catálogo de báscula a asignar. */
  scale_catalog_id?: number,
  /** Identificador del supervisor de báscula a asignar. */
  scale_supervisors_id?: number
}

/** Respuesta de la actualización masiva de tickets. */
export interface TicketsBulkUpdateRes {
  /** Indica si la operación produjo error. */
  error: boolean;
  /** Mensaje descriptivo del resultado. */
  message: string;
  /** Número de tickets actualizados. */
  updated_tickets: number;
  /** Campos efectivamente actualizados. */
  updated_fields: {
    ship_number?: number;
    ship_name?: string;
    lot_number?: number;
    scale_catalog_id?: number;
    scale_supervisors_id?: number;
  };
}
